import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { appointmentApi } from '../services/appointmentApi';
import { socketService } from '../services/socket';

/**
 * useEmodalSync
 *
 * Reconcile eModal's own "My Appointments" list against our records.
 *
 * Every other eModal integration we have only works at the moment of booking:
 * the receipt has to be caught in one tab, during one human-driven session, or
 * the appointment is lost to us for good. This is the path that still works
 * afterwards — it reads what eModal already holds, so it recovers bookings
 * made after a tab was closed, made directly on eModal, or made on a machine
 * that never had the extension.
 *
 * Flow: ask the backend where the list lives → hand that to the extension →
 * the extension reads the list and posts it back → the backend reconciles and
 * emits `emodal_sync_completed`, which is what resolves the toast here. The
 * page never sees the appointment data itself.
 */

const EXT_ID = import.meta.env.VITE_CHROME_EXT_ID || 'nniodjlikchoomnhdbcnlfncdcpodbib';
const EXT_PING_TIMEOUT_MS = 800;

/**
 * eModal's list can be slow on a busy account and the extension has its own
 * 60s give-up. Past this we stop claiming to be working — a spinner that
 * never resolves is what taught everyone to distrust the booking toast.
 */
const SYNC_WATCHDOG_MS = 3 * 60 * 1000;

export interface EmodalSyncChange {
  field: string;
  from: string | null;
  to: string | null;
  reason?: string;
}

export interface EmodalSyncSummary {
  scanned: number;
  linked: number;
  claimed: number;
  created: number;
  /** Rows where eModal disagreed with us and we took eModal's answer. */
  updated: number;
  /** Rows moved onto the eModal visit that replaced their own. */
  repointed: number;
  /** Rows we deliberately did NOT change — a human has to decide. */
  flagged: number;
  /** 按 eModal 把任务的码头改过来的那几张 —— 司机的终点变了。 */
  retargeted?: number;
  /** 核对到一半出错的那几张 —— 不是「对不上」，是这一张没核完。 */
  errored?: number;
  skippedOtherTerminal: number;
  skippedRevoked: number;
  /** True when this ran by itself after a booking, not from the button. */
  automatic?: boolean;
  details: {
    claimed: Array<{ containerNumber: string; appointmentId: number; taskId: number; ticket: string | null }>;
    created: Array<{ containerNumber: string; taskId: number; taskBid: string; ticket: string | null }>;
    updated: Array<{
      containerNumber: string;
      taskId: number;
      appointmentId: number;
      ticket: string | null;
      changes: EmodalSyncChange[];
      revoked: boolean;
    }>;
    repointed: Array<{
      containerNumber: string;
      taskId: number;
      appointmentId: number;
      oldTicket: string | null;
      ticket: string | null;
      changes: EmodalSyncChange[];
    }>;
    unmatched: Array<{
      containerNumber: string;
      ticket: string | null;
      reason: string;
      taskStatus?: string | null;
      /** 我们这边那一程写的码头 */
      taskTerminal?: string | null;
      /** eModal 上这张票所在的码头 */
      emodalTerminal?: string | null;
    }>;
    /** eModal appointments for containers this tenant has no record of at all. */
    untracked?: Array<{ containerNumber: string; ticket: string | null; reason: string }>;
    /** 那一程已经跑完、票的时段也过去了 —— 认不上，但也没什么可做的。 */
    settled?: Array<{ containerNumber: string; ticket: string | null; reason: string }>;
    /** 码头被改成了 eModal 那一家的任务。`from`/`to` 是码头代号。 */
    retargeted?: Array<{
      containerNumber: string;
      ticket: string | null;
      taskId: number;
      taskBid: string | null;
      from: string | null;
      to: string;
    }>;
    /** 同柜同码头同动作并存的旧票，让位给了最新那张。 */
    superseded?: Array<{
      containerNumber: string | null;
      ref: string | null;
      ticket: string | null;
      apptTypeCode: string | null;
    }>;
    /** 核对这一张的时候出错了 —— 可能已经改了一半，得有人去看。 */
    errored?: Array<{
      containerNumber: string | null;
      ticket: string | null;
      gateApptId?: number | null;
      reason: string;
    }>;
  };
  /** Count of the above — somebody else's moves on a shared eModal account. */
  untracked?: number;
  /** 让位给新票的旧票张数。悄悄丢掉和「读漏了」长得一样，所以要单独有个数。 */
  superseded?: number;
  /** Count of finished-and-expired legs. Deliberately kept out of `unmatched`. */
  settled?: number;
}

export interface EmodalSyncCompletionMeta {
  /**
   * True when this hook instance is the one that pressed the button.
   *
   * The event is company-scoped: an automatic reconcile after somebody else's
   * booking lands here too. Anything that closes UI on completion has to tell
   * the two apart, or a background run yanks a panel out from under whoever
   * happens to be reading it.
   */
  initiatedHere: boolean;
}

export interface UseEmodalSyncOptions {
  /** Fired once the backend has reconciled — callers refresh their lists here. */
  onCompleted?: (summary: EmodalSyncSummary, meta: EmodalSyncCompletionMeta) => void;
}

function pingExtension(timeoutMs: number): Promise<{ installed: boolean; enrolled: boolean }> {
  return new Promise((resolve) => {
    if (!window.chrome?.runtime?.sendMessage) {
      resolve({ installed: false, enrolled: false });
      return;
    }
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) { settled = true; resolve({ installed: false, enrolled: false }); }
    }, timeoutMs);
    try {
      chrome.runtime.sendMessage(EXT_ID, { type: 'ping' }, (resp: { ok?: boolean; state?: string } | undefined) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (chrome.runtime.lastError || !resp?.ok) {
          resolve({ installed: false, enrolled: false });
          return;
        }
        resolve({ installed: true, enrolled: resp.state === 'authenticated' });
      });
    } catch {
      if (!settled) { settled = true; clearTimeout(timer); resolve({ installed: false, enrolled: false }); }
    }
  });
}

function sendSyncStart(payload: { sessionId: string; listUrl: string; userJwt: string }):
  Promise<{ ok: boolean; error?: string }> {
  return new Promise((resolve) => {
    if (!window.chrome?.runtime?.sendMessage) {
      resolve({ ok: false, error: 'Chrome runtime unavailable' });
      return;
    }
    try {
      chrome.runtime.sendMessage(
        EXT_ID,
        { type: 'EMODAL_SYNC_START', ...payload },
        (resp: { ok?: boolean; error?: string } | undefined) => {
          if (chrome.runtime.lastError) resolve({ ok: false, error: chrome.runtime.lastError.message });
          else resolve({ ok: !!resp?.ok, error: resp?.error });
        }
      );
    } catch (e) {
      resolve({ ok: false, error: (e as Error).message });
    }
  });
}

/**
 * 同步跑完只说「码头动了我们的约、而且得有人接手」的那两类，其余一律不出声。
 *
 * 返回 null = 这一轮不值得打断任何人。列表照样刷新，只是没有弹窗。
 *
 * 这一刀分两次落下来。2026-09-29 先砍掉了自动跑：一天里每笔预约后面跟着两轮
 * 自动巡检，绿色的「我们替你改好了」只会把真正要紧的那条训练成「划过去」。
 * 2026-09-30 调度又指着一张**手动**同步的截图 —— 65 张里 12 行「eModal
 * corrected container_size」加一句「还有 42 条」，全是我们已经办好的事，
 * 还把数据库字段名怼到了人脸上。按没按过按钮不改变这件事的性质：办好了的不用
 * 汇报，办不了的失败中心本来就记着。成功不出声，只报没办成。
 *
 * 留下的两类是人必须自己动手、我们替不了的：
 *   - 约被码头毁了 → 要重约
 *   - 任务的终点被我们按 eModal 改了 → 司机要开去别处，改得对不对只有人知道
 *
 * 按钮按下去那一次并非没有回音：loading 那条 toast 会在这时消失，板子也刷新。
 */
export function describeSyncSummary(summary: EmodalSyncSummary): string | null {
  // 一柜一行（2026-09-14 拍板）—— 要看某只柜怎么了，不该先数一遍逗号。
  const lines: string[] = [];
  /** 没有柜号就不占一行：一行「 — CANCELLED…」什么都没说，却把有名字的挤下去。 */
  const line = (
    container: string | null | undefined,
    taskBid: string | null | undefined,
    what: string
  ) => {
    const who = (container || '').trim();
    if (!who) return;
    lines.push(`${taskBid ? `${who} · ${taskBid}` : who} — ${what}`);
  };

  // 核对到一半出错的排在最最前面：这几张我们**自己都不知道改到哪了**，
  // 而且很可能不是最后一张 —— 不说出来，屏幕上就只剩一片风平浪静。
  //
  // 这一类不走上面那个 line()：那个助手没柜号就不占行，而出错的票恰恰可能连
  // 柜号都还没读到。宁可写一行「有一张没核完」，也不能一声不吭。
  (summary.details?.errored || []).forEach((e) => {
    const who = (e.containerNumber || '').trim() || (e.ticket || '').trim();
    lines.push(
      `${who ? `${who} — ` : ''}could not be checked against eModal — `
      + 'some of it may already have been updated. See the failures centre.'
    );
  });

  // 取消排在最前面：这一行是最该被第一眼看到的。
  (summary.details?.updated || [])
    .filter((u) => u.revoked)
    .forEach((u) => line(u.containerNumber, null, 'CANCELLED at the terminal — rebook'));

  // 改了码头的那几张，和「加上了预约」是同一只柜的同一件事 —— 分两行写，同一只柜
  // 会在榜上出现两次。并成一句：先说终点变了（这是整轮里唯一会改变司机要开去哪里
  // 的事），再说预约加上了。纯粹「加上了预约」而没改终点的不上榜：那件事我们已经
  // 办完了，板子上就能看见。
  const retargeted = summary.details?.retargeted || [];
  const retargetOf = new Map(retargeted.map((r) => [r.containerNumber, r]));
  const foldedIn = new Set<string>();
  (summary.details?.created || []).forEach((c) => {
    const moved = retargetOf.get(c.containerNumber);
    if (!moved) return;
    foldedIn.add(c.containerNumber);
    line(
      c.containerNumber, c.taskBid,
      `now goes to ${moved.to} (was ${moved.from || 'another terminal'}) — appointment added from eModal`
    );
  });
  // 码头改了、预约却没落下来（写预约那步失败了）—— 这种更要说，任务的终点已经
  // 变了而手上还是没有时段。
  retargeted
    .filter((r) => !foldedIn.has(r.containerNumber))
    .forEach((r) => line(
      r.containerNumber, r.taskBid,
      `now goes to ${r.to} (was ${r.from || 'another terminal'}) to match eModal`
    ));

  if (!lines.length) return null;

  // Name the automatic runs as such: a dispatcher who didn't press anything
  // should not have to wonder where a toast about their appointments came from.
  const label = summary.automatic ? 'eModal auto-check' : 'eModal sync';

  // 行数上限：一次同步扫上百张，真出事时这里可能几十行。留一句「还有 N 条」，
  // 剩下的去失败中心看 —— toast 不该长到把屏幕占满。
  const MAX_LINES = 12;
  const shown = lines.slice(0, MAX_LINES);
  if (lines.length > MAX_LINES) {
    shown.push(`…and ${lines.length - MAX_LINES} more — see the failures centre`);
  }
  return [`${label} · ${summary.scanned} checked`, ...shown].join('\n');
}

export function useEmodalSync(options: UseEmodalSyncOptions = {}) {
  const { onCompleted } = options;
  const [syncing, setSyncing] = useState(false);

  const onCompletedRef = useRef(onCompleted);
  useEffect(() => { onCompletedRef.current = onCompleted; }, [onCompleted]);

  const toastIdRef = useRef<string | null>(null);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearWatchdog = () => {
    if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
  };

  useEffect(() => {
    const handleCompleted = (summary: EmodalSyncSummary) => {
      // A pending toast id is the only evidence that this tab started the run.
      const initiatedHere = toastIdRef.current !== null;
      clearWatchdog();
      if (toastIdRef.current) toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
      setSyncing(false);

      // null = 这一轮没有需要人接手的事（绝大多数轮次都是）。列表照样在下面
      // 刷新，只是不打断任何人。
      const spoken = describeSyncSummary(summary);
      if (spoken) {
        // 剩下会出声的都是「码头动了你的约」。这种停在屏幕上等人关掉，
        // 而不是几秒钟自己淡掉。
        // 一柜一行 —— 换行必须真的换行。sonner 把字符串当普通文本渲染，
        // 不给这条样式的话，十几行会被挤成一大坨。
        toast.warning(spoken, {
          duration: Infinity,
          closeButton: true,
          style: { whiteSpace: 'pre-line' },
        });
      }

      onCompletedRef.current?.(summary, { initiatedHere });
    };

    // socketService.on is typed (...args: unknown[]); widen at the boundary.
    const handler = handleCompleted as unknown as (...args: unknown[]) => void;
    socketService.on('emodal_sync_completed', handler);
    return () => {
      socketService.off('emodal_sync_completed', handler);
      clearWatchdog();
    };
  }, []);

  const startSync = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    const userJwt = localStorage.getItem('token');
    if (!userJwt) return { ok: false, error: 'Not signed in' };

    const { installed, enrolled } = await pingExtension(EXT_PING_TIMEOUT_MS);
    if (!installed) return { ok: false, error: 'EAloom extension not detected' };
    if (!enrolled) return { ok: false, error: 'EAloom extension not signed in — please sign in again' };

    let session;
    try {
      session = await appointmentApi.createEmodalSyncSession();
    } catch (e) {
      return { ok: false, error: (e as Error).message || 'Could not start eModal sync' };
    }

    setSyncing(true);
    toastIdRef.current = session.sessionId;
    // The tab is opened in the background and closed again by the extension —
    // nothing to click, nothing to keep open, stay where you are.
    toast.loading('Reading your eModal appointments…', { id: session.sessionId });

    const handoff = await sendSyncStart({ ...session, userJwt });
    if (!handoff.ok) {
      toast.dismiss(session.sessionId);
      toastIdRef.current = null;
      setSyncing(false);
      return { ok: false, error: handoff.error || 'Extension failed to start the sync' };
    }

    clearWatchdog();
    watchdogRef.current = setTimeout(() => {
      watchdogRef.current = null;
      if (toastIdRef.current !== session.sessionId) return;
      toast.dismiss(session.sessionId);
      toastIdRef.current = null;
      setSyncing(false);
      // ⚠️ 这条话曾经写着「Nothing was changed」——那是猜的，而且经常是错的。
      // 看门狗只知道「这么久没收到完成的信号」，不知道是插件没把列表送出去
      // （确实什么都没改），还是后端收下了、改到一半炸了（2026-09-30 线上就是
      // 后者：订单已经改完提交，柜子那一行才炸）。不知道的事不要替它下结论。
      toast.error(
        'eModal sync did not finish. We do not know how far it got — check that you are signed in '
        + 'to eModal, then look at the failures centre before running it again.',
        { duration: 10000 }
      );
    }, SYNC_WATCHDOG_MS);

    return { ok: true };
  }, []);

  return { startSync, syncing };
}

export default useEmodalSync;
